import React, { useCallback, useState } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { Button, LinkButton } from '@/atoms/Button'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { AsDiv, AsH2, AsP, HeadingXL, LabelMD, ParagraphLG, TitleSM } from '@/atoms/Text'
import { HeadingLG } from '@/atoms/Text'
import { BasicCarousel } from '@/molecules/BasicCarousel'
import { FloatingCallToActionDisplay } from '@/molecules/FloatingCallToAction/FloatingCallToActionDisplay'
import { getWebClient } from '@/services/ApolloClient'
import {
  CatalogTitle,
  CatalogTitleMap,
  formatDimensions,
  formatTitleLogo,
  CatalogMovie,
  formatTitleFriendlyReleaseDate,
} from '@/services/ContentCatalog'
import { ContentfulCloudinaryImageType } from '@/services/EmailCapturePageService/EmailCapturePageService'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { CallToAction, Faq } from '@/types/codegen-contentful'
import { buildCloudinaryImageUrlFromContentfulObject, getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { formatOrderSuffix, randomIntFromInterval } from '@/utils/numbers'
import { useTranslate } from '@/utils/translate/translate-client'
import { isDefined, isString } from '@/utils/types'
import { TrailerModal, TrailerModalProps } from '@/views/TicketsLandingView/TrailerModal'
import { formatShowtimesUrl } from '@/views/TicketsLandingView/transforms'
import { FaqSection } from '../WatchTabView/FaqSection'
import { PastReleaseCard } from './PastReleaseCard'
import { UpcomingReleaseCard } from './UpcomingReleaseCard'
import { TheatricalLandingReleaseNew } from './queries'
import { TitleRelease } from './types'

interface TicketsLandingViewProps {
  releases: TheatricalLandingReleaseNew[]
  titlesMap: CatalogTitleMap
  faqs: Faq[]
}

export function TicketsLandingViewNew({ releases, titlesMap, faqs }: TicketsLandingViewProps) {
  const { t } = useTranslate('tickets')
  const { isGuildMember } = useGuildMember()
  const { track } = useSafeAnalytics()
  const client = getWebClient()
  const { canClaimTickets } = useUserGuildTickets({ theatricalSlug: 'cabrini', client })
  const [trailerModal, setTrailerModal] = useState<TrailerModalProps | null>(null)

  const hasTrailer = trailerModal && Boolean(trailerModal?.titleRelease?.release?.filmDetails?.trailerSourceUrl)

  //TODO waiting on another experiment - ideally pull dynamic CTA
  const customCta = {
    buttonText: t('claimTickets', 'Claim Tickets'),
    buttonHref: '/tickets/cabrini?flow=guild',
    buttonIcon: 'ticket',
    description: 'March 8',
    descriptionColor: '#E9AE25',
    logoAltText: 'Cabrini Tickets',
    logoHeight: 20,
    logoUrl: '/v1688337569/angel-app/Cabrini/branding/Cabrini_Temp_Logo_White.svg',
    logoWidth: 150,
    title: 'In Theaters ',
  }

  const handleClick = useCallback(() => {
    track('Floating CTA Clicked')
  }, [track])

  const showClaimTickets = isGuildMember && canClaimTickets

  const handleCloseTrailer = useCallback(() => {
    setTrailerModal(null)
  }, [])

  const handleShowTrailer = useCallback(
    ({ titleRelease }: { titleRelease: TitleRelease }) => {
      setTrailerModal({ show: true, onClose: handleCloseTrailer, titleRelease })
    },
    [handleCloseTrailer],
  )

  return (
    <main className="pb-[128px]">
      <Hero
        titles={titlesMap}
        theatricalLandingReleases={filterReleases('in-theaters', releases).sort(byReleaseDateDesc)}
        onShowTrailer={handleShowTrailer}
      />
      <Upcoming
        titleReleases={zipTitleRelease(filterReleases('upcoming', releases).sort(byReleaseDateAsc), titlesMap)}
        onShowTrailer={handleShowTrailer}
      />
      <Past
        titleReleases={zipTitleRelease(filterReleases('past-release', releases).sort(byReleaseDateDesc), titlesMap)}
      />
      <Faqs faqs={faqs} />
      {showClaimTickets && (
        <FloatingCallToActionDisplay floatingCta={customCta as CallToAction} onClick={handleClick} position={'right'} />
      )}
      {hasTrailer && (
        <TrailerModal
          show={Boolean(trailerModal)}
          titleRelease={trailerModal.titleRelease}
          onClose={handleCloseTrailer}
        />
      )}
    </main>
  )
}

function filterReleases(releaseStatus: string, releases: TheatricalLandingReleaseNew[]) {
  return (releases ?? []).filter((r) => r?.releaseStatus === releaseStatus)
}

function zipTitleRelease(releases: TheatricalLandingReleaseNew[], titles: CatalogTitleMap): TitleRelease[] {
  return releases
    .map((release) => {
      const contentId = release?.contentCatalogId?.[0]
      const title = contentId ? (titles[contentId] as CatalogMovie) : undefined
      if (!title) return

      return { title, release }
    })
    .filter(isDefined)
}

interface HeroProps {
  theatricalLandingReleases: TheatricalLandingReleaseNew[]
  titles: CatalogTitleMap
  onShowTrailer: (args: { titleRelease: TitleRelease }) => void
}
function Hero(props: HeroProps) {
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()
  const { track } = useSafeAnalytics()

  // TODO: someday, support multiple simultaneous theatrical releases
  const release = props.theatricalLandingReleases[0]
  if (!release || !release.contentCatalogId || !isString(release.contentCatalogId[0])) return null
  const title = props.titles[release.contentCatalogId[0]] as CatalogMovie

  if (!title) return null
  const img = getRandomImage(release)

  return (
    <div
      className="relative w-full bg-cover bg-top bg-no-repeat py-[1px]"
      style={{
        backgroundImage: img ? `url(${buildCloudinaryImageUrlFromContentfulObject(img)})` : undefined,
      }}
    >
      <div className="m-4 mb-8 mt-[320px] flex max-w-[700px] flex-col gap-4 rounded-3xl bg-black/20 p-8 backdrop-blur-3xl sm:mt-[360px] sm:p-12 md:m-16 md:mt-[502px] md:gap-8 md:p-16 lg:m-12 lg:mt-[408px]">
        <Logo title={title} />
        <HeadingXL
          as={AsDiv}
          weight="bold"
          color="white"
          className="photon-title-lg lg:photon-heading-md 2xl:photon-heading-xl"
        >
          {t('inTheaters', 'In Theaters')} {formatInTheatersDate(locale, title)}
        </HeadingXL>
        <ParagraphLG
          as={AsP}
          className="photon-paragraph-sm md:photon-paragraph-md lg:photon-paragraph-lg leading-[26px]"
          weight="normal"
          color="white"
        >
          {title.description.short}
        </ParagraphLG>
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          <LinkButton
            href={formatShowtimesUrl(title)}
            variant="oxide-bright"
            className="flex h-12 items-center gap-[6px]"
          >
            <TicketIcon color="black" size={24} />
            <LabelMD color="black" weight="semibold">
              {t('getShowtimes', 'Get Showtimes')}
            </LabelMD>
          </LinkButton>
          <Button
            onClick={() => {
              track('Tickets Landing Hero CTA Clicked', { title, release })
              props.onShowTrailer({ titleRelease: { title, release } })
            }}
            variant="gray-600"
            className="flex h-12 items-center gap-[6px]"
          >
            <PlayIcon color="white" />
            <LabelMD color="white" weight="semibold">
              {t('watchSneakPeek', 'Watch Sneak Peek')}
            </LabelMD>
          </Button>
        </div>
      </div>
    </div>
  )
}

export function formatInTheatersDate(locale: string, title: CatalogMovie): string {
  const originalDate = new Date(title?.originalReleaseDate)

  if (originalDate < new Date()) return 'Now'

  return (
    formatTitleFriendlyReleaseDate(locale, title, {
      month: 'long',
      day: 'numeric',
    }) + (locale === 'en' ? formatOrderSuffix(originalDate.getUTCDate()) : '')
  )
}

// TODO: test and solve for hydration errors when multiple images
function getRandomImage(
  theatricalLandingRelease: TheatricalLandingReleaseNew,
): ContentfulCloudinaryImageType | undefined {
  const imgs = theatricalLandingRelease?.imagesCollection?.items
  if (!Array.isArray(imgs) || imgs.length === 0) return

  const count = imgs?.length ?? 0
  const i = randomIntFromInterval(0, count - 1)
  return imgs?.[i]?.item
}

function Logo(props: { title: CatalogTitle }) {
  const titleLogo = formatTitleLogo(props.title)
  if (!titleLogo) return null

  const url = getCloudinaryImageUrl({
    path: titleLogo.cloudinaryPath,
    transforms: 'e_trim',
    ...formatDimensions([570, 'auto'], titleLogo),
  })

  return (
    <h1 className="max-w-[570px]">
      <img src={url} alt={props.title.title} />
    </h1>
  )
}

interface UpcomingProps {
  titleReleases: TitleRelease[]
  onShowTrailer: (args: { titleRelease: TitleRelease }) => void
}
function Upcoming(props: UpcomingProps) {
  const { t } = useTranslate('tickets')
  return (
    <section className="flex flex-col gap-16">
      <div className="px-4">
        <Header
          title={t('upcomingReleases', 'Upcoming Releases')}
          tagline={t(
            'bigThings',
            "Big things are in the works. Don't miss these incredible upcoming films and shows from Angel Studios.",
          )}
        />
      </div>
      <BasicCarousel>
        {props.titleReleases.map((titleRelease) => (
          <UpcomingReleaseCard item={titleRelease} onShowTrailer={props.onShowTrailer} key={titleRelease.title.id} />
        ))}
      </BasicCarousel>
    </section>
  )
}

interface PastProps {
  titleReleases: TitleRelease[]
}
function Past(props: PastProps) {
  const { t } = useTranslate('tickets')
  return (
    <section className="flex flex-col items-center gap-16 px-6">
      <div className="flex flex-col items-center gap-4">
        <Header
          title={t('pastTheatricalReleases', 'Past Theatrical Releases')}
          tagline={t('discoverIncredibleTitles', 'Discover incredible titles available for Guild Early Access.')}
        />
        <LinkButton href="/guild/join" variant="white" className="inline-flex h-12 items-center gap-[6px]">
          <TicketIcon color="black" size={24} />
          <LabelMD color="black" weight="semibold">
            {t('joinTheGuild', 'Join the Guild')}
          </LabelMD>
        </LinkButton>
      </div>
      <ul className="min-[2000px]:grid flex grid-cols-2 flex-col justify-start gap-10">
        {props.titleReleases.map((titleRelease) => (
          <PastReleaseCard titleRelease={titleRelease} key={titleRelease.title.id} />
        ))}
      </ul>
    </section>
  )
}

interface HeaderProps {
  title: string
  tagline: string
}
function Header(props: HeaderProps) {
  return (
    <header className="mx-auto mb-4 flex flex-col items-center gap-[13px] pt-16 text-center lg:gap-4 lg:px-8 lg:pt-[128px]">
      <HeadingLG
        as={AsH2}
        weight="bold"
        color="white"
        className="md:photon-heading-xl lg:photon-display lg:text-[60px]"
      >
        {props.title}
      </HeadingLG>
      <TitleSM
        color="core-gray-400"
        weight="normal"
        className="md:photon-title-lg lg:photon-heading-sm max-w-[500px] text-pretty leading-[1.12em] lg:text-[28px]"
      >
        {props.tagline}
      </TitleSM>
    </header>
  )
}

function Faqs(props: { faqs: Faq[] }) {
  return (
    <section key="faqs" className="pb-40 pt-20">
      <div id="faqs" className="mx-auto max-w-[860px] scroll-mt-[73px] px-8 text-white">
        <LazyHydrate whenVisible>
          <FaqSection faqs={props.faqs} />
        </LazyHydrate>
      </div>
    </section>
  )
}

function byReleaseDateDesc(a: TheatricalLandingReleaseNew, b: TheatricalLandingReleaseNew) {
  return byReleaseDateAsc(b, a)
}

function byReleaseDateAsc(a: TheatricalLandingReleaseNew, b: TheatricalLandingReleaseNew) {
  if (!a?.filmDetails?.theatricalReleaseDate) return 1
  if (!b?.filmDetails?.theatricalReleaseDate) return -1
  return (
    new Date(a.filmDetails?.theatricalReleaseDate).getTime() - new Date(b.filmDetails?.theatricalReleaseDate).getTime()
  )
}
